import "./QuizResultsDetails.scss";

import {
  Alert,
  Avatar,
  Dropdown,
  DropdownItemProps,
  ExclamationTriangleIcon,
  Flex,
  Menu,
  Text,
} from "@fluentui/react-northstar";
import { TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO, SurveyResponsesDTO } from "@teams/types/api";
import React, { useState } from "react";

import { PollyIcon } from "../../../../../_shared/components/PollyIcon";
import { PagingControl } from "../PagingControl";
import { QuizLeaderboard } from "./QuizLeaderboard";
import { QuizQuestionResult } from "./QuizQuestionResult";
import { UserQuizResult } from "./UserQuizResult";

type Props = {
  survey: SurveyDTO;
  results: SurveyResponsesDTO;
  showPostResultsAction: boolean;
};

export const QuizResultsDetails: React.FC<Props> = ({ survey, results, showPostResultsAction }) => {
  const [activeTab, setActiveTab] = useState<"question" | "user" | "leaderboard">("leaderboard");
  const [activeUserResponse, setActiveUserResponse] = useState<string>();

  const items = [
    {
      key: "leaderboard",
      content: <PollyIcon icon="leaderboard-icon" label="Leaderboard" inheritColor />,
      onClick: () => setActiveTab("leaderboard"),
    },
    {
      key: "question",
      content: <PollyIcon icon="survey-question-icon" label="Question" inheritColor />,
      onClick: () => setActiveTab("question"),
    }
  ];

  if(survey.role === "own"){
    items.push( {
      key: "user",
      content: <PollyIcon icon="completions-icon" label="User" inheritColor />,
      onClick: () => setActiveTab("user"),
    },);
  }

  return (
    <Flex column gap="gap.medium" className="quiz-results-container">
      <Flex vAlign="center" gap="gap.small">
        <Menu
          underlined
          primary
          defaultActiveIndex={0}
          style={{ borderBottom: "unset", fontSize: "0.9em" }}
          items={items}
          activeIndex={items.findIndex(item => item.key === activeTab)}
          className="quiz-results-container__menu"
        />
      </Flex>

      {activeTab === "question" && <QuizQuestionsResults survey={survey} results={results} />}
      {activeTab === "user" && survey.role === 'own'  && (
        <QuizResultsList
          survey={survey}
          results={results}
          activeUserResponse={activeUserResponse}
        />
      )}
      {activeTab === "leaderboard" && (
        <QuizLeaderboard
          showPostResultsAction={showPostResultsAction}
          survey={survey}
          responses={results.responses}
          onSelect={(responseId: string) => {
            setActiveTab("user");
            setActiveUserResponse(responseId);
          }}
        />
      )}
    </Flex>
  );
};

const QuizQuestionsResults: React.FC<{ survey: SurveyDTO; results: SurveyResponsesDTO }> = ({
  survey,
  results,
}) => {
  const [page, setPage] = useState<number>(0);

  return (
    <Flex column gap="gap.small" className="questions-results-container">
      <Text content={survey.title} className="questions-results-container__survey-title" />
      <PagingControl
        count={survey.questions.length}
        current={page}
        onChange={value => setPage(value)}
      />
      <QuizQuestionResult
        survey={survey}
        question={survey.questions[page]}
        results={results}
        questionNo={page + 1}
      />
    </Flex>
  );
};

const QuizResultsList: React.FC<{
  survey: SurveyDTO;
  results: SurveyResponsesDTO;
  activeUserResponse: string | undefined;
}> = ({ survey, results, activeUserResponse }) => {
  const [selectedResponse, setSelectedResponse] = useState<TeamsSurveyResponse | undefined>(
    activeUserResponse
      ? results.responses.find(r => r.id === activeUserResponse)
      : results.responses[0]
  );

  if (results.responses.length === 0) {
    return <Alert icon={<ExclamationTriangleIcon />} content="No responses to this question" />;
  }

  return (
    <Flex column gap="gap.small" className="questions-results-container">
      <Text content={survey.title} className="questions-results-container__survey-title" />
      <Dropdown
        fluid
        value={selectedResponse?.user.name}
        items={results.responses.map(
          r =>
            ({
              content: (
                <Flex fill gap="gap.small" vAlign="center" key={`${r.id}-rankings-name-cell`}>
                  <Avatar
                    name={r.user.name}
                    size="small"
                    className="questions-results-container__avatar"
                  />
                  <Text content={r.user.name} className="questions-results-container__user-name" />
                </Flex>
              ),
              value: r.id,
            } as DropdownItemProps)
        )}
        onChange={(_, d) => {
          if (d?.value) {
            const response = results.responses.find(r => r.id === (d.value as any).value);
            setSelectedResponse(response);
          }
        }}
      />

      {!selectedResponse && <Alert danger content="Select a user" />}
      {selectedResponse && <UserQuizResult survey={survey} response={selectedResponse} />}
    </Flex>
  );
};
